import React from 'react';

import { Header, Icon, Segment } from 'semantic-ui-react';
import { clienteApi } from 'src/api';
import { Contexto } from 'src/contexto';
import { buscarPalabras, colapsarEspacios, sanearTildes } from 'src/utilidades';
import { MenuTabla, TablaApoderadosPlanesDePago } from './componentes';
import { Filtros, funciones as funcionesFiltro, Nombres } from './filtros';
import { Columna, DatosApoderado } from './tipos';

function normalizarRut(rut: string) {
    return rut.replace(/\./g, '').toLowerCase();
}

interface Datos {
    readonly porId: Record<string, Readonly<DatosApoderado>>;
    readonly todosLosId: ReadonlyArray<string>;
}

interface Props {
    readonly ocultar: boolean;
}

type State = Readonly<{
    datos: Datos;
    idsFiltrados: ReadonlyArray<string>;
    columnaAOrdenar: Columna;
    direccionDelOrden: 'ascending' | 'descending';
    cargando: boolean;
    textoBusqueda: string;
    filtros: Filtros;
}>;

class BuscarApoderado extends React.Component<Props, State> {
    public static readonly contextType = Contexto;
    public readonly context!: React.ContextType<typeof Contexto>;

    public readonly state: State = {
        datos: {
            porId: {},
            todosLosId: [],
        },
        idsFiltrados: [],
        columnaAOrdenar: 'nombre',
        direccionDelOrden: 'ascending',
        cargando: false,
        textoBusqueda: '',
        filtros: {
            deudores: 'todos',
            plan: 'todos',
            sede: 'todos',
        },
    };

    public componentDidMount() {
        if (!this.props.ocultar) {
            this.traerApoderados();
        }
    }

    public componentDidUpdate(prevProps: Props) {
        if (!this.props.ocultar && prevProps.ocultar) {
            if (this.state.datos.todosLosId.length === 0) {
                this.traerApoderados();
            } else {
                // Si bien en este caso no se necesita traer los datos otra vez,
                // si se debe limpiar el filtro de búsqueda.
                this.buscarEnFilas('');
            }
        }
    }

    public render() {
        if (this.props.ocultar) return null;

        const {
            datos,
            idsFiltrados,
            cargando,
            columnaAOrdenar,
            direccionDelOrden,
            filtros,
            textoBusqueda,
        } = this.state;

        const datosFiltrados =
            textoBusqueda.length >= 3 ? idsFiltrados.map(id => datos.porId[id]) : [];

        return (
            <div>
                <MenuTabla
                    cantidadDeApoderados={datosFiltrados.length}
                    cargando={cargando}
                    filtros={filtros}
                    alCambiarFiltros={this.cambiarFiltro}
                    alPresionarRecargar={this.traerApoderados}
                    alCambiarTextoDelBuscador={this.buscarEnFilas}
                />

                {textoBusqueda.length >= 3 ? (
                    <TablaApoderadosPlanesDePago
                        datos={datosFiltrados}
                        columnaAOrdenar={columnaAOrdenar}
                        direccionDelOrden={direccionDelOrden}
                        alClickearCabecera={this.manejarOrdenamiento}
                    />
                ) : (
                    <Segment attached='bottom' placeholder>
                        <Header icon size='small'>
                            <Icon name='search' />
                            Escriba al menos 3 caracteres en el cuadro de búsqueda para comenzar a
                            buscar.
                        </Header>
                    </Segment>
                )}
            </div>
        );
    }

    private readonly buscarEnFilas = (textoBusqueda: string) => {
        this.filtrarFilas(textoBusqueda, this.state.filtros);
    };

    private readonly cambiarFiltro = (filtro: Filtros) => {
        this.filtrarFilas(this.state.textoBusqueda, filtro);
    };

    private filtrarFilas(criterio: string, filtros: Filtros) {
        const { datos } = this.state;
        const { porId, todosLosId } = datos;

        const filtrados = todosLosId.filter(id =>
            (Object.keys(funcionesFiltro) as (keyof Nombres)[]).reduce<boolean>(
                (acc, nombreFiltro) => {
                    const funcionFiltro = funcionesFiltro[nombreFiltro];
                    // TS no puede inferir correctamente el tipo del filtro para la
                    // unión de funciones resultante.
                    // tslint:disable-next-line: no-any
                    return acc && funcionFiltro(porId[id], filtros[nombreFiltro] as any);
                },
                true,
            ),
        );

        const textoBusqueda = colapsarEspacios(criterio);

        const idsFiltrados =
            textoBusqueda !== ''
                ? filtrados.filter(id => {
                      const fila = datos.porId[id];
                      return (
                          buscarPalabras(
                              sanearTildes(fila.nombre).toLowerCase(),
                              sanearTildes(textoBusqueda).toLowerCase(),
                          ) ||
                          normalizarRut(fila.rut).startsWith(normalizarRut(textoBusqueda)) ||
                          fila.contratos.some(contr =>
                              contr.toString().startsWith(textoBusqueda),
                          ) ||
                          (fila.pagare && fila.pagare.toString().startsWith(textoBusqueda))
                      );
                  })
                : filtrados;

        this.setState({
            idsFiltrados,
            textoBusqueda,
            filtros,
        });
    }

    private readonly manejarOrdenamiento = (columnaClickeada: Columna) => {
        const { datos, idsFiltrados, columnaAOrdenar, direccionDelOrden } = this.state;

        const transformarRut = (rut: string) => {
            const rutSinDv = rut.split('-')[0] || '';
            const num = parseInt(rutSinDv.replace(/[^0-9]/g, ''), 10);
            return isNaN(num) ? 0 : num;
        };

        let nuevaDireccion: 'ascending' | 'descending' = 'ascending';
        let factorDeOrden = 1;

        if (columnaAOrdenar === columnaClickeada) {
            nuevaDireccion = direccionDelOrden === 'ascending' ? 'descending' : 'ascending';
            factorDeOrden = direccionDelOrden === 'ascending' ? -1 : 1;
        }

        // Convierte el arreglo de idsFiltrados en un arreglo de tuplas [id, indice].
        // El índice se usará para estabilizar el ordenamiento.
        const arregloDeTuplas = idsFiltrados.map((x, i) => [x, i] as [string, number]);

        function comparador(idA: string, idB: string) {
            const a = datos.porId[idA];
            const b = datos.porId[idB];

            if (columnaClickeada === 'rut') {
                return transformarRut(a.rut) - transformarRut(b.rut);
            }

            if (columnaClickeada === 'numeroCuotas') {
                return a.numeroCuotas - b.numeroCuotas;
            }

            if (columnaClickeada === 'valorCuota') {
                return a.valorCuota - b.valorCuota;
            }

            if (columnaClickeada === 'diaDePago') {
                return (a.diaDePago || 0) - (b.diaDePago || 0);
            }

            const c1: string = (a[columnaClickeada] || '').toString();
            const c2: string = (b[columnaClickeada] || '').toString();
            return c1.localeCompare(c2, 'es');
        }

        this.setState({
            idsFiltrados: arregloDeTuplas
                .sort(([idA, iA], [idB, iB]) => comparador(idA, idB) * factorDeOrden || iA - iB)
                .map(x => x[0]),
            columnaAOrdenar: columnaClickeada,
            direccionDelOrden: nuevaDireccion,
        });
    };

    private readonly traerApoderados = () => {
        this.setState({ cargando: true });
        clienteApi
            .ejecutar('obtenerTodosLosResumenesPlanesDePago')
            .then(({ apoderados }) => {
                const { porId, todosLosId } = apoderados;
                todosLosId.sort((idA, idB) =>
                    porId[idA].nombre.localeCompare(porId[idB].nombre, 'es'),
                );
                this.setState(
                    {
                        datos: { porId, todosLosId },
                        cargando: false,
                    },
                    () => {
                        this.filtrarFilas(this.state.textoBusqueda, this.state.filtros);
                    },
                );
            })
            .catch(error => {
                this.setState({ cargando: false });
                this.context.mostrarModalError(error);
            });
    };
}

export default BuscarApoderado;



// WEBPACK FOOTER //
// ./src/modulos/Cobranzas/componentes/BuscarApoderado/index.tsx